import { FC, useEffect, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import StreamAnimation, { AnimateContent } from '~src/shared/streamAnimation/StreamAnimation';
import ToggleableActivityList from '~src/modules/okrs/shared/activities/ToggleableActivityList';
import { StoreI } from '~src/rootReducer';
import {
  KeyResultsResponse,
  KeyResultUpdateInfo,
  OnSubmitUpdateKeyResult,
  ObjectiveI,
  KeyResultI,
} from '~src/store/okrs/keyResultStream/interfaces';
import { ApiReduxResp } from '~src/services/apiMiddleware';
import { CardsContainer } from '~src/modules/questions/components/shared.sc';
import { routes } from '~src/routingConfig';
import UpdateKeyResult from '~src/modules/okrs/objectivesStream/updateKeyResult/UpdateKeyResult';
import logger from '~src/services/logger/LlsLogger';
import { translateContent } from '~src/services/translationServices';
import LoadingCard from '~src/modules/questions/components/cards/LoadingCard';
import { ShowNotification } from '~src/store/app/interfaces';
import { showNotification } from '~src/store/app/actions';
import { mapIdToData } from '~src/utils';
import {
  fetchKeyResults,
  KeyResultFilterTypes,
  KeyResultQueryI,
  KeyResultUpdate,
  updateKeyResultAction,
} from '~src/store/okrs/keyResults/actions';

import { UpdateKeyResultContainer, Status, ListWrapper, ListContainer } from './keyResultsStreamContainer.sc';

interface StateProps {
  loadingKeyResults: boolean;
  keyResults?: KeyResultI[];
  objectives: { [key: string]: ObjectiveI };
}

interface DispatchProps {
  showNotification: ShowNotification;
  fetchKeyResults(objectiveId: string, query?: KeyResultQueryI): ApiReduxResp<KeyResultsResponse>;
  updateKeyResult(keyResultId: string, updateObj: KeyResultUpdate): ApiReduxResp<KeyResultsResponse>;
}

type Props = StateProps & DispatchProps & RouteComponentProps<{ objectiveId: string }>;

const KeyResultStreamContainer: FC<Props> = (props) => {
  const currentObjectiveId = props.match.params.objectiveId;
  const [t] = useTranslation();
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [KRLoaded, setKRLoaded] = useState(false);

  useEffect(() => {
    void handleFetching();
  }, [currentObjectiveId]);

  const handleFetching = () =>
    currentObjectiveId &&
    props.fetchKeyResults(currentObjectiveId, { [KeyResultFilterTypes.Stream]: 'true' }).then(() => {
      setKRLoaded(true);
    });

  const handleFinish = () => {
    const nextObjectiveId = getNextObjectiveId();
    props.history.push(
      routes.okrs.objectivesStream.finished +
        `?OID=${currentObjectiveId}` +
        (nextObjectiveId ? `&NOID=${nextObjectiveId}` : ''),
    );
  };

  const getNextObjectiveId = useCallback((): string | null => {
    const objectivesIds = Object.keys(props.objectives);
    if (!objectivesIds.length) {
      return null;
    }
    if (currentObjectiveId === null) {
      return objectivesIds[0];
    }
    const finishedIndex = objectivesIds.indexOf(currentObjectiveId);
    const nextIndex = (finishedIndex + 1) % objectivesIds.length; // cycled
    if (objectivesIds[nextIndex]) {
      return objectivesIds[nextIndex];
    }
    return null;
  }, [props.objectives, currentObjectiveId]);

  const renderErrorPage = (error: string) => (
    <div>
      <h1>{error}</h1>
      <button onClick={handleFetching}>{t('REFRESH:REPLACE')}</button>
    </div>
  );

  const updateKeyResult = ({ id, progressValue, comment }: KeyResultUpdateInfo) => {
    if (!Number.isInteger(progressValue)) {
      return Promise.reject('progress parameter is invalid (probably null / undefined)');
    }
    comment = comment ? comment.trim() : comment;
    return props
      .updateKeyResult(id, { comment, progress: progressValue })
      .then(({ error }) => {
        if (error) {
          throw new Error(error);
        }
      })
      .catch((error: Error) => {
        logger.info('Error updating key result', {
          error,
          info: {
            componentStack: 'KeyResultsStreamContainer.tsx',
          },
        });
        throw new Error('Error updating key result');
      });
  };

  if (!props.objectives[currentObjectiveId]) {
    return renderErrorPage('Invalid objective URL');
  }

  if (KRLoaded && props.keyResults && !props.keyResults.length && !props.loadingKeyResults) {
    // todo: implement screen
    // if no krs were found redirect to next objective
    return renderErrorPage(
      'Seems like there are no key results for current objective, move to next objective?',
    );
  }

  const firstUnanswered = 0; // todo: need to start always from 0 ? the backend should respond with the objectives krs that need to be updated, therefore the first kr need to be updated will be always the 1st

  const renderFooter: AnimateContent = (_, __, currentIndex) => {
    const keyResultId = props.keyResults ? props.keyResults[currentIndex].id : null;
    return (
      <>
        <Status>
          {t('x.okr.update_stream.objective_updated.remaining_krs', {
            objectiveName: translateContent(props.objectives[currentObjectiveId].name),
            KRsLeft: props.keyResults ? props.keyResults.length : 0,
          })}
        </Status>
        {keyResultId && (
          <ListContainer>
            <ListWrapper>
              <ToggleableActivityList id={keyResultId} type="key_result" />
            </ListWrapper>
          </ListContainer>
        )}
      </>
    );
  };

  return (
    <CardsContainer>
      <StreamAnimation
        max={props.keyResults ? props.keyResults.length : 0}
        startIndex={firstUnanswered}
        onFinish={handleFinish}
        renderFooter={renderFooter}
      >
        {(nextCard, __, currentIndex) => {
          const handleSubmit: OnSubmitUpdateKeyResult = (keyResultUpdateInfo) => {
            const errorKey = 'x.okr.update_stream.error.update_kr_failed';
            if (!loadingSubmit) {
              setLoadingSubmit(true);
              updateKeyResult(keyResultUpdateInfo)
                .then(() => {
                  nextCard();
                })
                .catch(() => {
                  props.showNotification({ type: 'error', id: errorKey, txtKey: errorKey, persistent: true });
                })
                .finally(() => {
                  setLoadingSubmit(false);
                });
            }
          };

          return (
            <UpdateKeyResultContainer>
              {!KRLoaded || props.loadingKeyResults ? (
                <LoadingCard />
              ) : (
                props.keyResults &&
                props.keyResults[currentIndex] && (
                  <UpdateKeyResult
                    id={props.keyResults[currentIndex].id}
                    objectiveId={props.match.params.objectiveId}
                    name={props.keyResults[currentIndex].name}
                    progressValue={props.keyResults[currentIndex].progress}
                    targetValue={props.keyResults[currentIndex].max}
                    startValue={props.keyResults[currentIndex].min}
                    onSubmitUpdateKeyResult={handleSubmit}
                    submitLoading={loadingSubmit}
                  />
                )
              )}
            </UpdateKeyResultContainer>
          );
        }}
      </StreamAnimation>
    </CardsContainer>
  );
};

const mapStateToProps = (state: StoreI): StateProps => ({
  loadingKeyResults: state.okrs.keyResults.loadingKeyResults,
  keyResults: state.okrs.keyResults.keyResults,
  objectives: state.okrs.objectives.objectivesListData
    ? mapIdToData(state.okrs.objectives.objectivesListData.items)
    : {},
});

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  fetchKeyResults: (objectiveId, query) => dispatch(fetchKeyResults(objectiveId, query)),
  updateKeyResult: (keyResultId, updateObj) => dispatch(updateKeyResultAction(keyResultId, updateObj)),
  showNotification: (notification) => dispatch(showNotification(notification)),
});

export default connect(mapStateToProps, mapDispatchToProps)(KeyResultStreamContainer);
